<template>
  <div class="faq-page p-4">
    <div class="max-w-5xl mx-auto">
      <div class="flex align-items-center gap-3 mb-4">
        <i class="pi pi-question-circle text-3xl text-primary"></i>
        <div>
          <h1 class="m-0 text-2xl font-bold">Вопрос-Ответ</h1>
          <p class="m-0 text-surface-500">Подробное FAQ по разделам и возможностям системы</p>
        </div>
      </div>

      <Card class="mb-4">
        <template #content>
          <div class="flex flex-column gap-2">
            <label for="faq-search" class="text-sm font-medium text-surface-700">Поиск по вопросам и ответам</label>
            <span class="p-input-icon-left w-full">
              <i class="pi pi-search" />
              <InputText
                id="faq-search"
                v-model="query"
                class="w-full"
                placeholder="Например: синхронизация, импорт, изображения, ошибки, 409, таймаут"
                autocomplete="off"
              />
            </span>
            <div class="text-sm text-surface-500">
              Показано: <strong>{{ visibleQuestionsCount }}</strong> из <strong>{{ totalQuestionsCount }}</strong>
            </div>
          </div>
        </template>
      </Card>

      <Message v-if="filteredSections.length === 0" severity="info" :closable="false" class="mb-4">
        <i class="pi pi-info-circle mr-2"></i>
        Ничего не найдено. Попробуйте изменить запрос.
      </Message>

      <Accordion v-else multiple :activeIndex="activeSectionIndexes">
        <AccordionTab
          v-for="(section, sectionIndex) in filteredSections"
          :key="section.key"
          :header="section.title + ' (' + section.items.length + ')'"
        >
          <div class="flex flex-column gap-3">
            <Accordion multiple>
              <AccordionTab
                v-for="(item, itemIndex) in section.items"
                :key="section.key + ':' + itemIndex"
                :header="item.q"
              >
                <div class="faq-answer whitespace-pre-line">
                  {{ item.a }}
                </div>
              </AccordionTab>
            </Accordion>
          </div>
        </AccordionTab>
      </Accordion>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import Message from 'primevue/message'

const query = ref('')

const FAQ_SECTIONS = [
  {
    key: 'overview',
    title: 'Общее',
    items: [
      {
        q: 'Что это за система и для чего она нужна?',
        a: 'Это платформа для управления товарной базой (в т.ч. очки/оптика) и связанных процессов: загрузка и нормализация данных из файлов, редактирование карточек, управление изображениями, аналитика/отчёты, синхронизация с PartsSoft, а также инструменты для администрирования фоновых задач и логов.'
      },
      {
        q: 'Какие разделы есть в меню и за что они отвечают?',
        a: 'Кратко:\n- Чат — запросы к данным «человеческим языком», выгрузки в Excel.\n- Файлы — импорт Excel/таблиц, сопоставление колонок и значений, контроль ошибок импорта.\n- Данные — просмотр и редактирование товаров/атрибутов в таблице.\n- Изображения — галерея/управление изображениями и привязками.\n- Учет оплат — загрузка/обновление оплат и статусов по заказам.\n- Вознаграждения — расчёт и контроль вознаграждений (например, Luxottica).\n- Синхронизация — интеграция с PartsSoft (категории/атрибуты/товары) и прогресс.\n- Администрирование — фоновые задачи, пауза/возобновление, чистка логов (для админов).\n- Вопрос-Ответ — справка и типовые решения.'
      },
      {
        q: 'Нужно ли что-то делать перед началом работы?',
        a: 'Обычно достаточно войти под своей учётной записью. Для синхронизации с PartsSoft часто требуется выбрать магазин (если включён мульти‑стор), а для импорта — подготовить файл с корректными колонками (например, part_number и другие обязательные поля).'
      },
      {
        q: 'Какие роли пользователей бывают?',
        a: 'В текущем интерфейсе встречаются роли: Поддержка (support) и Администратор (admin). Администратору доступен раздел «Администрирование» и связанные действия с фоновыми задачами.'
      }
    ]
  },
  {
    key: 'chat',
    title: 'Чат (AI)',
    items: [
      {
        q: 'Зачем нужен раздел «Чат»?',
        a: 'Чат позволяет задавать вопросы к базе данных естественным языком. В ответ система может вернуть: (1) текстовое объяснение, (2) SQL‑запрос (если он формировался), (3) таблицу результатов, (4) кнопку для скачивания результата в Excel.'
      },
      {
        q: 'Какие вопросы лучше задавать в чате?',
        a: 'Лучше всего работают конкретные запросы: «покажи товары без изображений», «найди дубликаты по артикулу», «выгрузи товары бренда X за последние 30 дней», «какие категории пустые», «сколько товаров в архиве». Чем точнее условия (магазин, период, бренд, статус), тем стабильнее результат.'
      },
      {
        q: 'Почему в ответе иногда показывается SQL?',
        a: 'SQL отображается для прозрачности: можно понимать, какие данные запрашивались, и при необходимости уточнять вопрос (например, добавить фильтры по статусу/магазину).'
      },
      {
        q: 'Что делать, если в чате пришёл пустой результат?',
        a: 'Проверьте условия: возможно, слишком жёсткие фильтры или в базе реально нет подходящих строк. Попробуйте упростить запрос (убрать часть условий) и затем уточнить снова.'
      },
      {
        q: 'Что делать, если чат возвращает ошибку?',
        a: 'Обычно помогает: (1) переформулировать запрос проще, (2) уточнить период/магазин, (3) проверить авторизацию (если ошибка 401), (4) при повторяемой ошибке — обратиться к администратору и приложить текст запроса и время ошибки.'
      }
    ]
  },
  {
    key: 'files',
    title: 'Файлы (импорт)',
    items: [
      {
        q: 'Как устроен типовой импорт из Excel?',
        a: 'Типовой поток: загрузить файл → сопоставить колонки (Column Mapping) → при необходимости настроить маппинг значений (Value Mapping) → выполнить нормализацию → запустить импорт/обновление → скачать отклонённые строки (если есть) и исправить исходный файл.'
      },
      {
        q: 'Что означает сообщение «Состояние сохранено — прогресс импорта не потеряется»?',
        a: 'Импорт может быть многошаговым. Система сохраняет состояние процесса, поэтому вы можете временно перейти в другие разделы и вернуться — прогресс/настройки импорта останутся.'
      },
      {
        q: 'Почему часть строк попадает в «ненайденные/отклонённые»?',
        a: 'Частые причины: отсутствует обязательное поле (например, part_number), неверный формат данных (число/дата/текст), некорректные значения атрибутов, конфликт ключей, или строка не проходит валидацию. Такие строки можно скачать, исправить и импортировать повторно.'
      },
      {
        q: 'Что такое «сопоставление колонок» и зачем оно нужно?',
        a: 'В разных файлах названия колонок отличаются. Сопоставление позволяет привязать колонки файла к полям системы (например, «Артикул» → part_number, «Бренд» → brand и т.д.), чтобы импорт корректно заполнил нужные поля.'
      },
      {
        q: 'Что такое «маппинг значений» и когда он нужен?',
        a: 'Маппинг значений применяется, когда в файле встречаются разные варианты одного смысла (например, «муж», «М», «male») — вы задаёте каноническое значение (например, «МУЖСКИЕ»). Эти правила сохраняются и помогают стабилизировать данные на следующих импортов.'
      },
      {
        q: 'Что делать, если импорт «долго крутится» или пишет про таймаут?',
        a: 'Иногда операции (особенно массовые) могут занимать много времени. Если интерфейс сообщает о долгом запросе/таймауте, проверьте логи сервера: обработка могла завершиться в фоне. Если результат не применился — повторите операцию или выполните импорт меньшими партиями.'
      },
      {
        q: 'Можно ли импортировать изображения через раздел «Файлы»?',
        a: 'В зависимости от сценария, через импорт можно передавать URL изображений (например, колонка с ссылками через запятую). Далее изображения/ссылки будут обработаны согласно логике системы и могут появиться в разделе «Изображения» и в карточках товаров.'
      }
    ]
  },
  {
    key: 'data',
    title: 'Данные (таблица товаров)',
    items: [
      {
        q: 'Что можно делать в разделе «Данные»?',
        a: 'Раздел предназначен для просмотра и редактирования товарных данных в табличном виде: поиск и правки полей, работа с изображениями (URL списком), массовые операции (если доступны), а также запуск связанных действий (например, экспорт/обогащение — если включено в UI).'
      },
      {
        q: 'Почему некоторые поля нельзя менять или они «возвращаются назад»?',
        a: 'Часть полей может быть защищённой (protected) или управляться синхронизацией/правилами. Если поле защищено, система может игнорировать изменения или пересинхронизировать значение. В таких случаях уточните, какое поле и на каком этапе нужно менять (вручную, через импорт, через PartsSoft или через правила маппинга).'
      },
      {
        q: 'Как правильно редактировать изображения в карточке товара?',
        a: 'Если интерфейс принимает изображения как список URL через запятую — добавляйте ссылки аккуратно, без лишних пробелов/неполных URL. После сохранения проверьте в разделе «Изображения», что ссылки корректно обрабатываются.'
      },
      {
        q: 'Есть ли быстрый способ массово исправить данные?',
        a: 'Для массовых исправлений чаще всего удобнее использовать импорт (раздел «Файлы») с правильно подготовленной таблицей и маппингом. Так вы повторяемо примените правки и уменьшите риск ручных ошибок.'
      }
    ]
  },
  {
    key: 'images',
    title: 'Изображения',
    items: [
      {
        q: 'Зачем отдельный раздел «Изображения»?',
        a: 'Он помогает централизованно управлять изображениями: просматривать, искать проблемы (дубликаты, отсутствующие изображения), контролировать привязки к товарам и общую целостность медиаданных.'
      },
      {
        q: 'Почему появляются дубликаты изображений?',
        a: 'Дубликаты возникают при повторных импортов, разных URL на один и тот же файл или при загрузке в разные папки/регистры. Система может содержать механизмы дедупликации/нормализации; при систематической проблеме стоит проверить правила импорта и синхронизации изображений.'
      },
      {
        q: 'Что делать, если изображение не отображается?',
        a: 'Проверьте: (1) URL доступен в браузере, (2) нет лишних пробелов/символов, (3) права доступа к хранилищу, (4) не истёк ли временный токен (если используется). Если проблема массовая — проверьте настройки S3/хранилища и логи сервера.'
      }
    ]
  },
  {
    key: 'payments',
    title: 'Учет оплат',
    items: [
      {
        q: 'Что делает раздел «Учет оплат»?',
        a: 'Раздел предназначен для синхронизации и анализа данных по оплатам/заказам: загрузка оплат, обновление статусов, просмотр отчётов и выгрузка в Excel (если доступно в интерфейсе).'
      },
      {
        q: 'Как часто выполняется «Полная синхронизация» оплат?',
        a: 'В интерфейсе есть подсказка, что полная синхронизация запускается автоматически ежедневно в 9:00 по МСК, а также её можно запустить вручную, если нужно обновить данные сейчас.'
      },
      {
        q: 'Чем отличается «Полная синхронизация» от «Синхронизации статусов»?',
        a: 'Полная синхронизация подтягивает и обновляет данные шире (заказы/позиции), а синхронизация статусов фокусируется на обновлении статусов уже известных позиций. Точную разницу можно понимать как «полное обновление» vs «быстрое обновление состояния».'
      }
    ]
  },
  {
    key: 'rewards',
    title: 'Вознаграждения',
    items: [
      {
        q: 'Для чего нужен раздел «Вознаграждения»?',
        a: 'Он помогает вести учёт вознаграждений по заказам (например, Luxottica): видеть заказы/позиции с вознаграждениями, отмечать выплаты и контролировать расхождения.'
      },
      {
        q: 'Почему часть строк подсвечивается или имеет особый статус?',
        a: 'Как правило, выделение используется для быстрой диагностики: например, невыплаченные вознаграждения или строки с аномалиями. Смысл подсветки обычно описан в самом разделе или виден по полям статуса.'
      }
    ]
  },
  {
    key: 'sync',
    title: 'Синхронизация с PartsSoft',
    items: [
      {
        q: 'Что синхронизируется с PartsSoft?',
        a: 'Основные блоки: категории, атрибуты и товары. Обычно «Синхронизировать всё» последовательно выполняет синхронизацию категорий и атрибутов, а затем запускает синхронизацию товаров (прогресс может показываться в отдельном окне).'
      },
      {
        q: 'Зачем выбирать магазин?',
        a: 'В мульти‑стор режиме синхронизация и данные могут отличаться по магазинам/локалям. Выбор магазина определяет контекст: какие категории/атрибуты/товары синхронизировать и какие настройки применять.'
      },
      {
        q: 'Что означает ошибка «Синхронизация уже запущена» (конфликт/409)?',
        a: 'Это защита от параллельных запусков. Если синхронизация уже идёт, второй запуск блокируется. Откройте «Прогресс синхронизации» и дождитесь завершения, либо остановите/поставьте на паузу через админ‑инструменты (если есть права).'
      },
      {
        q: 'Где смотреть прогресс синхронизации?',
        a: 'В разделе «Синхронизация» есть кнопка «Прогресс синхронизации». Там отображаются этапы, текущий статус, сообщения и итог.'
      },
      {
        q: 'Что делать, если синхронизация зависла или долго не меняется прогресс?',
        a: 'Проверьте: (1) нет ли лимитов/таймаутов на стороне PartsSoft, (2) логи сервера и фоновых задач, (3) сеть/доступность API. Если прогресс не обновляется, но задача может идти — подождите и затем проверьте отчёты/логи. При необходимости используйте паузу/возобновление через админ‑панель.'
      },
      {
        q: 'Почему после синхронизации часть данных «не совпадает»?',
        a: 'Причины: конфликт правил (protected поля), разные источники истинности (локальная база vs PartsSoft), задержки в обработке, несовпадение маппинга категорий/атрибутов. В таких случаях обычно помогают отчёты синхронизации и повторный запуск синхронизации с нужными настройками.'
      }
    ]
  },
  {
    key: 'admin',
    title: 'Администрирование',
    items: [
      {
        q: 'Что показывает раздел «Администрирование»?',
        a: 'Он показывает статус планировщика (APScheduler), список задач, а также позволяет запускать/ставить на паузу/возобновлять отдельные фоновые задачи (в пределах прав администратора).'
      },
      {
        q: 'Для чего нужна пауза/возобновление задач?',
        a: 'Это полезно, когда нужно временно остановить тяжёлую синхронизацию или обслуживание (например, на время импорта или диагностики), а затем продолжить без потери контекста.'
      },
      {
        q: 'Что такое чистка логов синхронизации?',
        a: 'Логи со временем разрастаются. В админ‑панели обычно есть инструмент для очистки старых записей по сроку хранения (retention). Это снижает нагрузку на базу и упрощает сопровождение.'
      }
    ]
  },
  {
    key: 'troubleshooting',
    title: 'Ошибки и диагностика',
    items: [
      {
        q: '401/403: почему меня «выкидывает» или не пускает в раздел?',
        a: 'Чаще всего токен авторизации истёк или был удалён. Выйдите и войдите заново. Если доступ ограничен ролью (например, «Администрирование»), проверьте роль пользователя.'
      },
      {
        q: 'Запрос выполняется слишком долго: что делать?',
        a: 'Для больших объёмов это ожидаемо. Рекомендации: (1) ограничивайте период/магазин/условия, (2) выполняйте импорт/обновление частями, (3) проверяйте логи — задача могла завершиться на сервере, даже если UI получил таймаут.'
      },
      {
        q: 'Как правильно обращаться в поддержку/к администратору при ошибке?',
        a: 'Минимум: (1) раздел и что делали, (2) точный текст ошибки/скрин, (3) пример входных данных (файл/строка), (4) время ошибки и выбранный магазин. Это сильно ускоряет поиск причины.'
      }
    ]
  }
]

const totalQuestionsCount = computed(() => {
  return FAQ_SECTIONS.reduce((acc, section) => acc + section.items.length, 0)
})

const normalizedQuery = computed(() => query.value.trim().toLowerCase())

const filteredSections = computed(() => {
  const q = normalizedQuery.value
  if (!q) {
    return FAQ_SECTIONS
  }

  const matches = (text) => String(text || '').toLowerCase().includes(q)

  return FAQ_SECTIONS
    .map((section) => {
      const items = section.items.filter((item) => matches(item.q) || matches(item.a) || matches(section.title))
      return { ...section, items }
    })
    .filter((section) => section.items.length > 0)
})

const visibleQuestionsCount = computed(() => {
  return filteredSections.value.reduce((acc, section) => acc + section.items.length, 0)
})

// Раскрываем все секции, если есть поиск; иначе не раскрываем ничего.
const activeSectionIndexes = ref([])
watch(
  () => normalizedQuery.value,
  (q) => {
    if (q) {
      activeSectionIndexes.value = filteredSections.value.map((_, idx) => idx)
    } else {
      activeSectionIndexes.value = []
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.faq-answer {
  color: var(--text-color);
  line-height: 1.5;
}

/* PrimeVue input icon wrapper is global; keep only page-level tweaks */
.faq-page :deep(.p-accordion-header-link) {
  line-height: 1.25;
}
</style>
